import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Scramble } from "@/components/Scramble";
import { Magnetic } from "@/components/Magnetic";

const HERO_IMG =
  "https://images.unsplash.com/photo-1714009889233-6699f04623ff?q=85&w=2400&auto=format&fit=crop";

const LINES = ["Your Freight.", "Our Priority.", "Your Success."];

const lineReveal = {
  hidden: { x: "-115%", opacity: 0, filter: "blur(8px)" },
  visible: (i) => ({
    x: "0%",
    opacity: 1,
    filter: "blur(0px)",
    transition: { delay: 1.6 + i * 0.42, duration: 1.6, ease: [0.16, 1.3, 0.32, 1] },
  }),
};

const SkidDust = ({ delay }) => (
  <span className="absolute -bottom-2 right-2 pointer-events-none" aria-hidden="true">
    <motion.span
      className="block h-[2px] w-14 bg-gradient-to-l from-gold/60 to-transparent"
      initial={{ opacity: 0, scaleX: 0.2 }}
      animate={{ opacity: [0, 0.8, 0], scaleX: 1 }}
      transition={{ delay, duration: 0.7, times: [0, 0.2, 1], ease: "easeOut" }}
      style={{ transformOrigin: "right" }}
    />
    {[...Array(6)].map((_, p) => (
      <motion.span
        key={p}
        className="absolute bottom-0 right-0 block rounded-full"
        initial={{ opacity: 0, scale: 0.2, x: 0, y: 0 }}
        animate={{
          opacity: [0, 0.9, 0],
          scale: [0.2, 1, 2.2],
          x: [0, 8 + p * 9, 18 + p * 16],
          y: [0, -(2 + (p % 3) * 3), 5 + (p % 2) * 4],
        }}
        transition={{ delay: delay + p * 0.02, duration: 0.85, times: [0, 0.25, 1], ease: "easeOut" }}
        style={{
          width: 5 + (p % 3) * 2,
          height: 5 + (p % 3) * 2,
          background: "radial-gradient(circle, rgba(243,213,139,0.85), rgba(212,175,55,0.3) 55%, transparent 70%)",
          filter: "blur(1px)",
        }}
      />
    ))}
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-24">
        <motion.img
          src={HERO_IMG}
          alt="Dark semi-truck with amber marker lights parked on a night lot"
          className="w-full h-full object-cover"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1.02, opacity: 1 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
        />
      </motion.div>
      <div className="absolute inset-0 hero-vignette" />
      <div className="headlight-sweep" aria-hidden="true" />
      <div className="absolute bottom-0 inset-x-0 h-28 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="lane lane-a" />
        <div className="lane lane-b" />
      </div>

      <motion.div style={{ opacity: fade }} className="relative px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto w-full pt-32 pb-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.0, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8"
        >
          <Logo size="lg" testId="hero-logo" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 0.8 }}
          className="overline-label mb-12 sm:mb-6"
        >
          <Scramble text="B&H Truck Dispatching" />
        </motion.p>

        <div className="relative inline-block">
          <motion.span
            data-testid="hero-graffiti-tag"
            initial={{ opacity: 0, scale: 0.6, rotate: -14 }}
            animate={{ opacity: 1, scale: 1, rotate: -5 }}
            transition={{ delay: 2.9, type: "spring", stiffness: 220, damping: 11 }}
            className="font-graffiti graffiti-glow absolute -top-9 right-0 text-xl sm:text-3xl lg:text-4xl sm:top-[30%] sm:-right-8 lg:-right-24 text-gold whitespace-nowrap z-10"
          >
            Every Mile Matters!
          </motion.span>
          <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-5xl sm:text-7xl lg:text-8xl">
            {LINES.map((line, i) => (
              <span key={line} className="relative block pb-1">
                <motion.span
                  custom={i}
                  variants={lineReveal}
                  initial="hidden"
                  animate="visible"
                  className={`block ${i === 2 ? "gold-text" : "text-white"}`}
                >
                  {line}
                </motion.span>
                <SkidDust delay={2.85 + i * 0.42} />
              </span>
            ))}
          </h1>
        </div>

        <div className="gold-line-grow mt-6 h-[2px] w-40 sm:w-56 bg-gradient-to-r from-gold via-champagne to-transparent" />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.2, duration: 0.8 }}
          className="mt-6 text-base md:text-lg text-champagne font-medium tracking-wide"
          data-testid="hero-subheadline"
        >
          Reliable Dispatching. Strategic Load Planning. Unmatched Carrier Support.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.35, duration: 0.8 }}
          className="mt-5 max-w-xl text-sm sm:text-base text-zinc-400 leading-relaxed"
        >
          At B&amp;H Truck Dispatching, we understand that every mile matters. We help carriers stay focused on the road
          while we handle the coordination, communication, and logistics that keep their businesses moving forward.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Magnetic>
            <Link to="/become-a-carrier" data-testid="hero-get-started-btn" className="btn-gold">
              Get Started Today
            </Link>
          </Magnetic>
          <Magnetic strength={0.25}>
            <Link to="/#services" data-testid="hero-explore-services-btn" className="btn-outline-gold">
              Explore Our Services
            </Link>
          </Magnetic>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gold/70"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ChevronDown size={26} />
        </motion.div>
      </motion.div>
    </section>
  );
};
